// ============================================================
// src/lib/themeUtils.js
//
// ── WHAT THIS FILE DOES ──────────────────────────────────────
// Central registry and helpers for the Drag Helper theme system.
//
// Four themes are available:
//   its-pink    — Original. Hot pink, deep plum, yellow. Font: Fredoka One.
//   green-gush  — Energetic, positive, creative. Purple, vivid green, yellow. Font: Teko.
//   bone         — Relatable, authentic, entertained. Deep purple, crimson, lavender. Font: New Rocker.
//   blue-shock  — Electric, bold, entertaining. Electric blue, ocean teal, gold. Font: Bebas Neue.
//
// Theme is applied via  data-theme="<id>"  on  <html>.
// localStorage keeps it across refreshes; Supabase user_metadata
// keeps it across devices (ThemeSelector handles the Supabase write).
//
// ── HOW TO INIT ──────────────────────────────────────────────
// Call  initTheme()  once at the very top of main.jsx / App.jsx,
// BEFORE the React render, so the correct theme is applied before
// the first paint and there is no flash of the default pink theme:
//
//   import { initTheme } from './lib/themeUtils'
//   initTheme()
//   ReactDOM.createRoot(document.getElementById('root')).render(<App />)
//
// ── HOW TO READ THE ACTIVE THEME ─────────────────────────────
//   import { getSavedTheme } from '../lib/themeUtils'
//   const theme = getSavedTheme() // returns theme id string
// ============================================================

/** localStorage key used to persist the active theme id. */
export const THEME_STORAGE_KEY = 'dh_theme'

/**
 * Full theme registry.
 *
 * Fields used by ThemeSelector:
 *   id            — matches the data-theme attribute value and CSS selector
 *   name          — display name shown in the picker card
 *   font          — CSS font-family string (the display / heading font)
 *   fontWeight    — weight for the preview title text
 *   description   — one-line personality description
 *   previewBg     — CSS gradient for the preview panel (uses theme's raw colors)
 *   previewText   — hex color for preview text (white or near-white/light)
 *   swatches      — 3 hex colors shown as a strip below the preview panel
 *                   ordered: primary, secondary, accent
 */
export const THEMES = [
  {
    id:          'its-pink',
    name:        "It's Pink!",
    font:        'Fredoka One',
    fontWeight:  400,
    description: 'the original.',
    previewBg:   ' #ff69b4',
    previewText: '#ffffff',
  },
  {
    id:          'green-gush',
    name:        'Green Gush',
    font:        'Teko',
    fontWeight:  600,
    description: 'Good Vibes',
    previewBg:   ' #16a34a',
    previewText: '#ffffff',
  },
  {
    id:          'bone',
    name:        'Bone',
    font:        'New Rocker',
    fontWeight:  400,
    description: 'Dark Mode',
    previewBg:   ' #1a0a1e ',
    previewText: '#f5f0fa',
  },
  {
    id:          'blue-shock',
    name:        'Blue Shock',
    font:        'Bebas Neue',
    fontWeight:  400,
    description: '"Blue is my favorite color" -Perk',
    previewBg:   ' #0047AB',
    previewText: '#ffffff',
  },
]

// ── Lookup map for O(1) access by id ─────────────────────────
export const THEME_MAP = Object.fromEntries(THEMES.map(t => [t.id, t]))


// ── Storage helpers ───────────────────────────────────────────

/**
 * Returns the persisted theme id from localStorage,
 * falling back to 'its-pink' if nothing has been saved.
 */
export function getSavedTheme() {
  return localStorage.getItem(THEME_STORAGE_KEY) || 'its-pink'
}

/**
 * Sets  data-theme  on <html> — the single source of truth for
 * CSS variable overrides. Does NOT write to localStorage.
 */
export function applyTheme(themeId) {
  document.documentElement.setAttribute('data-theme', themeId)
}

/**
 * Persists the theme to localStorage and applies it to the DOM.
 * ThemeSelector also writes to Supabase user_metadata for cross-device sync.
 */
export function saveTheme(themeId) {
  localStorage.setItem(THEME_STORAGE_KEY, themeId)
  applyTheme(themeId)
}

/**
 * Reads the saved theme from localStorage and applies it.
 * Call once at app startup before the first React render to
 * prevent a flash of the default pink theme.
 */
export function initTheme() {
  applyTheme(getSavedTheme())
}
